import { EqualApproximatelyIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  FieldSelect,
  OperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  AttributeCompareProps,
  AttributeCompareShape,
  attributeCompareType,
  ComparisonOperator,
  comparisonOperators,
  Dimensions,
  Field,
  fieldOptions,
  fields,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { ListItem, UnorderedList } from "@/components/ui/typography";

const defaultDescription =
  "Allows comparing an attribute of a record to a target value.";

export function isAttributeCompareShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is AttributeCompareShape {
  return shape.type === attributeCompareType;
}

export class AttributeCompareTool extends ToolStateNode {
  static override id = attributeCompareType;

  static override uiToolDef = (editor: Editor) => ({
    id: attributeCompareType,
    icon: EqualApproximatelyIcon,
    label: AttributeCompareShapeUtil.docs.title,
    kbd: "a",
    defaultColor: AttributeCompareShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(attributeCompareType),
    docs: () => (
      <NodeDocs
        editor={editor}
        util={AttributeCompareShapeUtil}
        component={AttributeCompare}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: attributeCompareType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class AttributeCompareShapeUtil extends BindableUtil<AttributeCompareShape> {
  static override migrations = createMigrations(attributeCompareType);
  static override type = attributeCompareType;

  static override docs = {
    defaultColor: "teal" as const,
    title: "Attribute Comparison",
    description: (
      <div>
        <div>{defaultDescription}</div>
        <UnorderedList className="ml-1">
          <ListItem className="my-1">
            <span className="font-black">text</span> - The text content of the
            post.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">reply</span> - A reference to the
            parent post. if any.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">embed.external.uri</span> - A url
            embedded in the post, if any.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">langs[0]</span> - The primary language
            the post was authored in.
          </ListItem>
        </UnorderedList>
      </div>
    ),
  };

  override getType() {
    return attributeCompareType;
  }

  static override props: RecordProps<AttributeCompareShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    value: T.nullable(T.or(T.string, T.number)),
    operator: T.literalEnum(...comparisonOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 440,
      h: MIN_H,
      var: fields[0],
      value: "alf",
      operator: fieldOptions[0]?.validOperators[0] || comparisonOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: AttributeCompareShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <AttributeCompare
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function AttributeCompare({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: AttributeCompareShape;
  className?: string;
  updateShape: (
    shape: AttributeCompareShape,
    props: Partial<AttributeCompareProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          AttributeCompareShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={AttributeCompareShapeUtil.docs}
        />
        <BlockBody>
          <FieldSelect
            value={shape.props.var}
            onValueChange={(v) => {
              const defaultOperators = fieldOptions.find(
                (f) => f.value === v,
              )?.validOperators;
              updateShape(shape, {
                var: v as Field,
                operator: defaultOperators?.[0] || shape.props.operator,
              });
            }}
          />
          <OperatorSelect
            value={shape.props.operator}
            validOperators={
              fieldOptions.find((f) => f.value === shape.props.var)
                ?.validOperators
            }
            onValueChange={(v) =>
              v && updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message="The value to compare the attribute against.">
            <CanvasInput
              aria-label="Test value"
              value={shape.props.value || ""}
              className="flex-1"
              placeholder="null"
              onValueChange={(value) => updateShape(shape, { value })}
              required={false}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
